import { useState, useCallback, useRef } from 'react';

interface ProductImageProps {
  title: string;
  categoryName: string;
  imageUrl: string;
  webpImageUrl: string;
  hasImage: boolean;
  isEditMode?: boolean;
  imagePreviewUrl?: string | null;
  onImageSelect?: (file: File) => void;
}

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export function ProductImage({
  title,
  categoryName,
  imageUrl,
  webpImageUrl,
  hasImage,
  isEditMode = false,
  imagePreviewUrl,
  onImageSelect,
}: ProductImageProps) {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback((file: File) => {
    if (ACCEPTED_TYPES.includes(file.type)) {
      onImageSelect?.(file);
    }
  }, [onImageSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    // Reset so the same file can be re-selected
    e.target.value = '';
  }, [handleFile]);

  const displayUrl = imagePreviewUrl || (hasImage ? imageUrl : null);
  const displayWebp = imagePreviewUrl ? null : (hasImage ? webpImageUrl : null);

  if (!isEditMode) {
    return (
      <div className="flex items-center justify-center rounded-lg border border-gray-200 bg-white p-5">
        {hasImage ? (
          <picture>
            <source srcSet={webpImageUrl} type="image/webp" />
            <img
              src={imageUrl}
              alt={`${title} - ${categoryName}`}
              title={`${title} fresco - Delivery en Córdoba`}
              className="max-h-[350px] w-full object-contain"
            />
          </picture>
        ) : (
          <div className="flex h-[200px] w-[200px] items-center justify-center">
            <i className="fa fa-leaf text-gray-300" style={{ fontSize: '80px' }} />
          </div>
        )}
      </div>
    );
  }

  // Edit mode
  return (
    <div
      className={`relative flex cursor-pointer items-center justify-center rounded-lg border-2 border-dashed bg-white p-5 transition-colors ${
        isDragging
          ? 'border-green-500 bg-green-50'
          : imagePreviewUrl
            ? 'border-amber-400'
            : 'border-green-400/60 hover:border-green-500'
      }`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={handleFileChange}
      />

      {displayUrl ? (
        <>
          {displayWebp ? (
            <picture>
              <source srcSet={displayWebp} type="image/webp" />
              <img
                src={displayUrl}
                alt={`${title} - ${categoryName}`}
                className="max-h-[350px] w-full object-contain"
              />
            </picture>
          ) : (
            <img
              src={displayUrl}
              alt={`${title} - ${categoryName}`}
              className="max-h-[350px] w-full object-contain"
            />
          )}
          {/* Overlay hint */}
          <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/0 opacity-0 transition-opacity hover:bg-black/30 hover:opacity-100">
            <span className="rounded-lg bg-white/90 px-4 py-2 text-sm font-medium text-gray-700 shadow">
              <i className="fa fa-camera mr-2" />Cambiar imagen
            </span>
          </div>
        </>
      ) : (
        <div className="flex h-[200px] flex-col items-center justify-center gap-3 text-gray-400">
          <i className="fa fa-cloud-upload text-4xl" />
          <span className="text-sm">Arrastrá una imagen o hacé clic</span>
        </div>
      )}
    </div>
  );
}
